import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { FacetSelected } from '../models/facet-selected-model';
import { MetadataProp } from '../models/metadata-prop-model';
import { QueryViewModel } from '../models/query-view-model';
import { SearchComboItemModel } from '../models/search-combo-item-model';
import { SearchComboModel } from '../models/search-combo-model';
import { SearchFilter } from '../models/search-filter-model';
import { SearchRequestModel } from '../models/search-request-model';
import { SortDetails } from '../models/sort-details-model';
import { MetadataService } from './metadata.service';
import { ConstantsService } from './constants.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class QueryStateService {

  private formData?: any;
  private formDataString = '';
  private queryString: string = '';
  private queryView: QueryViewModel = { allProps: [], anyProps: [], proceedingStatuses: [], library: [] };
  private mdItems?: SearchComboModel[];
  private facetitems: SearchFilter[] = [];
  private queryFilters: SearchFilter[] = []; //Original Search Filters And
  private queryAnyFilters: SearchFilter[] = []; //Original Search Filters Any
  private sortBy?: string = '';
  private sortDir?: number = 1;
  private searchRequest: SearchRequestModel = { q: '', filters: [], indexId: 'EHD', openProceedings: true, closedProceedings:true }; //Search Request Based on the Form Data

  private querySubject = new BehaviorSubject<QueryViewModel>({});
  qData$ = this.querySubject.asObservable();

  constructor(private mdSvc: MetadataService, private constants: ConstantsService) {
    this.mdItems = this.mdSvc.mdItems;
  }

  //Behavior subject
  setqData(data: QueryViewModel) {
    this.querySubject.next(data);
  }

  getqData(): QueryViewModel {
    return this.querySubject.value;
  }

  //Used to return the columns for the table View based on the search
  getSearchFilters() {
    return this.queryFilters.concat(this.queryAnyFilters);
  }

  getQueryString() {
    return this.queryString;
  }

  updateQueryString(value: string) {
    this.queryString = value;
  }

  getFormData() {
    return this.formData;
  }

  getFormDataString() {
    return this.formDataString;
  }

  updateFormData(value: FormGroup) {
    this.formData = value;
    this.formDataString = JSON.stringify(this.formData);
    this.parseSearchForm();
  }

  //Used to display details for saved search
  parseToQueryView(fd: any): QueryViewModel {

    let qv: QueryViewModel = {};

    qv.allProps = [];
    qv.anyProps = [];
    qv.proceedingStatuses = [];
    qv.library = [];

    qv.keywords = fd.keywords;

    let allProps = fd[this.constants.property_operator_all];
    let anyProps = fd[this.constants.property_operator_any];

    if (anyProps) {
      qv.anyProps = this.processQueryViewProperties(anyProps);
    }

    if (allProps) {
      qv.allProps = this.processQueryViewProperties(allProps);
    }

    if (fd.openProceedings) {
      qv.proceedingStatuses.push("Open Proccedings");
    }

    if (fd.closedProceedings) {
      qv.proceedingStatuses.push("Closed Proceedings");
    }

    if (fd.indexId === this.constants.indexEHD) {
      qv.library.push(this.constants.display_Name_EHD);
    }

    if (fd.indexId === this.constants.indexSDM) {
      qv.library.push(this.constants.display_Name_SDM);
    }

    return qv;
  }

  processQueryViewProperties(searchProps: any): any[] {
    let propsArray: any[] = [];

    searchProps.forEach((i: SearchComboItemModel) => {
      if (i.propertyItem != '') {
        let p = this.mdItems?.find((m) => m.id == i.propertyItem) as MetadataProp;
        if (p) {
          propsArray.push({
            displayName: p.displayName, keywords: i.keywords,
            isDate: i.isDate, startDate: i.startDate, endDate: i.endDate,
            dateOperator: i.dateOperator, textOperator: i.textOperator
          });
        }
      }
    });

    return propsArray;
  }

  getQueryView() {
    this.queryView = this.parseToQueryView(this.formData);
    this.setqData(this.queryView);
    return this.queryView;
  }

  updateMetadataProps(items: MetadataProp[]) {
    this.mdItems = items;
  }

  getMetadataProps() {
    return this.mdItems;
  }

  clearSelectedFacets() {
    this.facetitems = [];
  }

  updateSelectedFacets(item: FacetSelected) {
    // Get all the items for the same facet name
    this.facetitems = this.facetitems.filter(
      function (value, index, arr) {
        return value.field != item.field
      });

    // Add the items in the new items list
    item.values.forEach((i) => {
      let sf: SearchFilter = {
        field: item.field,
        value: i
      };
      this.facetitems.push(sf);
    });
  }

  updateSelectedSort(sortItem: SortDetails) {
    this.sortBy = sortItem.field;
    this.sortDir = sortItem.order;
  }

  resetSort() {
    this.sortBy = '';
    this.sortDir = 1;
  }

  // Returns the Original Search request with original Filters based on the FormData
  getSearchRequest(): SearchRequestModel {
    return this.searchRequest;
  }

  // returns a Search request with the selected facets.
  getUpdatedSearchRequest(): SearchRequestModel {
    // Form the Search Request using the original Query and the facets
    let combinedFilters = this.queryFilters.concat(this.facetitems);
    this.searchRequest.filters = combinedFilters;
    this.searchRequest.sort = this.sortBy;
    this.searchRequest.sortDirection = this.sortDir;
    return this.searchRequest;
  }

  updateSkipCount(count: number) {
    this.searchRequest.skip = count;
  }

  async parseSearchForm() {
    let q = '';

    // console.log("Form Data: " + JSON.stringify(this.formData));
    // TODO: Check if formData null and redirect if not????
    if (this.formData) {
      let k = this.formData['keywords'];
      if (k) {
        q = k;
      }

      let anyProps = this.formData[this.constants.property_operator_any];
      let allProps = this.formData[this.constants.property_operator_all];

      this.searchRequest.q = q;
      this.searchRequest.filters = this.parseFromProps(allProps);
      this.searchRequest.anyFilters = this.parseFromProps(anyProps);
      this.searchRequest.indexId = this.formData['indexId'];
      this.searchRequest.openProceedings = this.formData['openProceedings'];
      this.searchRequest.closedProceedings = this.formData['closedProceedings'];
      this.queryFilters = this.searchRequest.filters;
      this.queryAnyFilters = this.searchRequest.anyFilters;
      
      // console.log("sr: " + JSON.stringify(this.searchRequest));
    }
  }

  parseFromProps(props: any): SearchFilter[] {
    let f: SearchFilter[] = [];

    // Parse the property Filters and add to the query request
    props.forEach((i: SearchComboItemModel) => {
      if (i.propertyItem != '') {
        //Lookup the CogSearchProperty
        let p = this.mdItems?.find((m) => m.id == i.propertyItem) as MetadataProp;

        // Test if the keywords is empty or if the property item is empty
        if (p && p.cogSearchKey) {
          if (i.isDate) {
            if (i.startDate && i.dateOperator) { //Validate if the date values exists.  
              f.push(this.parseDateFilter(p.cogSearchKey, i));
            }
          }
          else {
            i.keywords = i.keywords?.trim();
            if (i.keywords) {
              f.push({ field: p.cogSearchKey, value: i.keywords, operator: i.textOperator });
            }
          }
        }
      }
    });

    return f;
  }

  private splitPhrases(data: string): string[] {
    let matches: string[] = [];
    let regex = /"([^"]+)"|([^ ]+)/g;
    let match;

    while ((match = regex.exec(data)) !== null) {
      // matches.push(match[1] || match[2]);
      matches.push(match[0]);
    }

    return matches;
  }

  private splitOperators(data: string): string[] {
    let matches: string[] = [];
    let regex = /("[^"]+")|([^ ]+)|and|or/gi;
    let match;

    while ((match = regex.exec(data)) != null) {
      matches.push(match[0]);
    }

    return matches;
  }

  // Used by the facets
  parseDateFilter(fieldName: string, item: SearchComboItemModel): SearchFilter {
    let filter: SearchFilter = { field: '' };
    let dateFilter = '';

    if (item.dateOperator == 'between' && !item.endDate) {
      item.dateOperator = 'starts';
    }

    // Validate Start/End Dates
    if (item.dateOperator == 'between') {
      dateFilter = "(" + fieldName + " ge '" + item.startDate + "' and " + fieldName + " le '" + item.endDate + "')";
    }
    else if (item.dateOperator == 'starts') {

      dateFilter = "(" + fieldName + " ge '" + item.startDate + "')";
    }
    else if (item.dateOperator == 'ends') {
      dateFilter = "(" + fieldName + " le '" + item.startDate + "')";
    }
    else if (item.dateOperator == 'equals') {
      dateFilter = "(" + fieldName + " eq '" + item.startDate + "')";
    }

    filter.field = fieldName;
    filter.value = dateFilter;

    return filter;
  }

  // Format the Display when a new Facet is added.  
  displayFilters(data: SearchFilter[]): string {
    let filterString = '';

    data.forEach((f) => {
      filterString = filterString + ' ' + f.field + ' =  ' + '"' + f.value + '", ';
    });

    // return JSON.stringify(data);
    filterString = filterString.substring(0, filterString.length - 2);
    return filterString;
  }
}
